import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  createCamera,
  createCameraFailure,
  createCameraSuccess,
  updateCamera,
  updateCameraFailure,
  updateCameraSuccess,
  deleteCamera,
  deleteCameraFailure,
  deleteCameraSuccess,
  listCameraSets,
  listCameraSetsFailure,
  listCameraSetsSuccess,
} from '@state/camera-sets/camera-sets.actions';
import { Camera } from '@state/camera-sets/camera-sets.interfaces';
import { switchMap } from 'rxjs/operators';


@Injectable()
export class CameraSetsEffects {
  actions$ = inject(Actions);
  api = inject(ApiService);
  handler = inject(EffectHandlerService);

  listCameraSets$ = createEffect(() => this.actions$.pipe(
    ofType(listCameraSets),
    switchMap(() => this.api.listCameraSets().pipe(
      this.handler.effectResponse({
        retry: listCameraSets(),
        success: (cameraSets) => listCameraSetsSuccess({ cameraSets }),
        failure: listCameraSetsFailure(),
        failureMessage: 'Could not load Camera Sets',
      })
    ))
  ));

  createCameraSet$ = createEffect(() => this.actions$.pipe(
    ofType(createCamera),
    switchMap(({ cameraSet, camera }) => this.api.createCamera({ cameraSet, camera }).pipe(
      this.handler.effectResponse({
        retry: createCamera({ cameraSet, camera }),
        success: (_camera: Camera) => _camera.name
          ? createCameraSuccess({ cameraSet, camera: _camera })
          : createCameraFailure(),
        failure: createCameraFailure(),
        failureMessage: 'Could not create Camera',
        successMessage: 'Camera created successfully'
      })
    ))
  ));

  updateCameraSet$ = createEffect(() => this.actions$.pipe(
    ofType(updateCamera),
    switchMap(({ cameraSet, camera }) => this.api.updateCamera(camera).pipe(
      this.handler.effectResponse({
        retry: updateCamera({ cameraSet, camera }),
        success: (_camera: Camera) => _camera.name
          ? updateCameraSuccess({ cameraSet, camera: _camera })
          : updateCameraFailure(),
        failure: updateCameraFailure(),
        failureMessage: 'Could not update Camera',
        successMessage: 'Camera updated successfully'
      })
    ))
  ));

  deleteCameraSet$ = createEffect(() => this.actions$.pipe(
    ofType(deleteCamera),
    switchMap(({ cameraSet, uuid }) => this.api.deleteCamera(uuid).pipe(
      this.handler.effectResponse({
        retry: deleteCamera({ cameraSet, uuid }),
        success: () => deleteCameraSuccess({ cameraSet, uuid }),
        failure: deleteCameraFailure(),
        failureMessage: 'Could not delete Camera',
        successMessage: 'Camera deleted successfully'
      })
    ))
  ));

}
